import { Button, Typography } from "@platform/design-system";
import { Box, CircularProgress, Stack } from "@mui/material";
import { Fragment } from "react";

import { ThreadRow } from "../ThreadRow";

import type { ThreadSummary } from "../../threadSummary";

export interface ThreadListProps {
  threads: ThreadSummary[];
  expandedThreadId?: string;
  onToggleThread: (threadId: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** True once a search has narrowed the list, which changes the empty copy. */
  isFiltered?: boolean;
  /** Present while the service has more pages to hand over. */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /**
   * The conversation to show under the expanded row.
   *
   * A render prop rather than data, so reading a thread stays the host app's
   * job: this library takes plain values and never a query client, which is
   * what lets its stories and tests run without one.
   *
   * `collapse` closes this row. It is handed over rather than left to the
   * caller because which row is open is this list's state, and a host that
   * had to track it too would be a second place for the two to disagree.
   */
  renderThreadBody?: (
    threadId: string,
    collapse: () => void,
  ) => React.ReactNode;
}

/**
 * The rows, and the three states that are not rows.
 *
 * An empty list after a search and an empty list on arrival read the same to
 * the eye and mean opposite things — nothing matched what you typed, against
 * nothing has arrived yet — so they get different copy.
 */
export const ThreadList = ({
  threads,
  expandedThreadId,
  onToggleThread,
  isLoading = false,
  isError = false,
  onRetry,
  isFiltered = false,
  onLoadMore,
  isLoadingMore = false,
  renderThreadBody,
}: ThreadListProps): React.JSX.Element => {
  if (isLoading) {
    return (
      <Centered testId="thread-list-loading">
        <CircularProgress aria-label="Loading threads" />
      </Centered>
    );
  }

  if (isError) {
    return (
      <Centered testId="thread-list-error">
        <Typography variant="body-medium">
          These messages could not be loaded.
        </Typography>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Centered>
    );
  }

  if (threads.length === 0) {
    return (
      <Centered testId="thread-list-empty">
        <Typography variant="body-medium" color="textSecondary">
          {isFiltered
            ? "No messages or threads match your search."
            : "No messages yet. Mail about this RFP will appear here."}
        </Typography>
      </Centered>
    );
  }

  return (
    <Box data-testid="thread-list" sx={{ flexGrow: 1, overflowY: "auto" }}>
      {threads.map((thread) => (
        <Fragment key={thread.id}>
          <ThreadRow
            thread={thread}
            expanded={thread.id === expandedThreadId}
            onToggle={onToggleThread}
          />
          {thread.id === expandedThreadId &&
            renderThreadBody?.(thread.id, () => onToggleThread(thread.id))}
        </Fragment>
      ))}

      {onLoadMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Button
            variant="secondary"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          >
            {isLoadingMore ? "Loading…" : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const Centered = ({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Stack
    data-testid={testId}
    spacing={2}
    sx={{
      flexGrow: 1,
      alignItems: "center",
      justifyContent: "center",
      p: 4,
      textAlign: "center",
    }}
  >
    {children}
  </Stack>
);
